import React, { useState, useRef, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { apiCreate, apiList, apiUpdate } from '../../api/apiClient';
import { refreshAllCounters } from '../../utils/refreshCounters';

const DEVICE_TYPE_INFO: Record<string, { icon: string; label: string; desc: string }> = {
  relay: { icon: '🔌', label: 'Bridge Relay', desc: 'Relay controller for door strikes and gates' },
  panel: { icon: '📋', label: 'Intercom Panel', desc: 'Touchscreen intercom with video and face recognition' },
  doorbell: { icon: '🔔', label: 'Bridge Doorbell', desc: 'Audio doorbell with camera and face recognition' },
  hub: { icon: '📡', label: 'Camera Hub', desc: 'Camera aggregator for IP cameras and NVRs' },
  camera: { icon: '📷', label: 'Bridge Camera', desc: 'ESP32-CAM module for entry monitoring' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  properties: any[];
  onDeviceAdded: () => void;
}

type Step = 'scan' | 'manual' | 'confirm' | 'property' | 'name' | 'done';

export const DeviceOnboardWizard: React.FC<Props> = ({ open, onClose, properties, onDeviceAdded }) => {
  const [step, setStep] = useState<Step>('scan');
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState<any>(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [deviceName, setDeviceName] = useState('');
  const [manualMac, setManualMac] = useState('');
  const [manualType, setManualType] = useState('relay');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (open) {
      setStep('scan');
      setScannedData(null);
      setError('');
      setDeviceName('');
      if (properties.length > 0) setSelectedPropertyId(properties[0].uuid || String(properties[0].id));
    }
    return () => stopCamera();
  }, [open]);

  // ── Camera QR Scanner ──

  const startCamera = async () => {
    setScanning(true);
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: 640, height: 480 },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        requestAnimationFrame(scanFrame);
      }
    } catch (e) {
      setError('Camera access denied. Use manual entry instead.');
      setScanning(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const scanFrame = () => {
    if (!videoRef.current || !canvasRef.current || !scanning) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) {
      requestAnimationFrame(scanFrame);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    // Use BarcodeDetector API if available (Chrome 83+)
    if ('BarcodeDetector' in window) {
      const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
      detector.detect(canvas).then((barcodes: any[]) => {
        if (barcodes.length > 0) {
          const payload = barcodes[0].rawValue;
          handleQrScanned(payload);
          return;
        }
        if (scanning) requestAnimationFrame(scanFrame);
      }).catch(() => {
        if (scanning) requestAnimationFrame(scanFrame);
      });
    } else {
      // Fallback: manual entry
      if (scanning) requestAnimationFrame(scanFrame);
    }
  };

  const handleQrScanned = (payload: string) => {
    stopCamera();

    // Parse intrlock://device/{type}/{mac}/{verification}
    const match = payload.match(/^intrlock:\/\/device\/(\w+)\/([\da-f:]+)\/([a-f0-9]{6})$/i);
    if (!match) {
      setError(`Invalid QR code: "${payload}". Expected an Intrlock device QR code.`);
      return;
    }

    setScannedData({
      device_type: match[1],
      mac_address: match[2],
      verification_code: match[3],
      device_id: `${match[1]}-${match[2].replace(/:/g, '')}`,
      qr_payload: payload,
    });

    const info = DEVICE_TYPE_INFO[match[1]];
    setDeviceName(info?.label || `Bridge ${match[1]}`);
    setStep('confirm');
  };

  const handleManualEntry = () => {
    if (!manualMac.match(/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/i)) {
      setError('Invalid MAC address format. Use XX:XX:XX:XX:XX:XX');
      return;
    }

    setScannedData({
      device_type: manualType,
      mac_address: manualMac.toLowerCase(),
      verification_code: 'manual',
      device_id: `${manualType}-${manualMac.replace(/:/g, '').toLowerCase()}`,
      qr_payload: `intrlock://device/${manualType}/${manualMac.toLowerCase()}/manual`,
    });

    const info = DEVICE_TYPE_INFO[manualType];
    setDeviceName(info?.label || `Bridge ${manualType}`);
    setStep('confirm');
  };

  const handleAddDevice = async () => {
    if (!scannedData || !selectedPropertyId) return;
    setSaving(true);
    try {
      // Check if device already exists
      const existing = await apiList('bridge_devices');
      const found = (existing || []).find((d: any) =>
        d.mac_address === scannedData.mac_address || d.serial_number === scannedData.mac_address.replace(/:/g, '')
      );

      if (found) {
        // Update existing device with new property assignment
        await apiUpdate('bridge_devices', found.uuid || found.id, {
          property_id: selectedPropertyId,
          name: deviceName,
          device_type: scannedData.device_type,
          status: 'pending',
        });
      } else {
        // Create new device record
        await apiCreate('bridge_devices', {
          name: deviceName,
          device_type: scannedData.device_type,
          mac_address: scannedData.mac_address,
          serial_number: scannedData.mac_address.replace(/:/g, ''),
          property_id: selectedPropertyId,
          verification_code: scannedData.verification_code,
          qr_payload: scannedData.qr_payload,
          status: 'pending',
          connection_status: 'offline',
        });
      }

      refreshAllCounters();
      setStep('done');
      onDeviceAdded();
    } catch (e) {
      setError('Failed to register device. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { stopCamera(); onClose(); }} title="Add Bridge Device" size="lg">

      {/* ═══ SCAN STEP ═══ */}
      {step === 'scan' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">Scan the QR code on the bottom of your Intrlock Bridge device, or enter the device MAC address manually.</p>

          {/* Camera viewport */}
          <div className="relative bg-black rounded-xl overflow-hidden" style={{ minHeight: 300 }}>
            {scanning ? (
              <>
                <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                <canvas ref={canvasRef} className="hidden" />
                {/* Scan overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-white/50 rounded-2xl relative">
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-xl" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-xl" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-xl" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-xl" />
                    {/* Scan line animation */}
                    <div className="absolute left-2 right-2 h-0.5 bg-emerald-400/80 animate-bounce" style={{ top: '50%' }} />
                  </div>
                </div>
                <button onClick={stopCamera} className="absolute top-3 right-3 p-2 rounded-full bg-black/50 text-white hover:bg-black/70">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-white/70">
                <span className="text-5xl mb-4">📱</span>
                <p className="text-sm mb-4">Point your camera at the device QR code</p>
                <Button onClick={startCamera}>Start Scanning</Button>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{error}</p>}

          <div className="text-center">
            <button onClick={() => setStep('manual')} className="text-sm text-brand-600 hover:underline">
              Or enter device details manually
            </button>
          </div>
        </div>
      )}

      {/* ═══ MANUAL ENTRY ═══ */}
      {step === 'manual' && (
        <div className="space-y-4">
          <button onClick={() => setStep('scan')} className="text-xs text-brand-600 hover:underline flex items-center gap-1 mb-2">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back to scanner
          </button>
          <h3 className="font-semibold text-gray-900">Manual Device Entry</h3>
          <p className="text-sm text-gray-500">Find the MAC address on the device label (bottom of the unit).</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Device Type</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(DEVICE_TYPE_INFO).filter(([k]) => k !== 'camera').map(([key, info]) => (
                <button key={key} onClick={() => setManualType(key)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${manualType === key ? 'border-brand-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-xl">{info.icon}</span>
                  <p className="text-xs font-medium text-gray-900 mt-1">{info.label}</p>
                </button>
              ))}
            </div>
          </div>

          <Input label="MAC Address" placeholder="dc:a6:32:12:ab:cd" value={manualMac}
            onChange={(e: any) => setManualMac(e.target.value)} />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button onClick={handleManualEntry} disabled={!manualMac}>Continue</Button>
        </div>
      )}

      {/* ═══ CONFIRM DEVICE ═══ */}
      {step === 'confirm' && scannedData && (
        <div className="space-y-4">
          <div className="bg-green-50 rounded-xl p-5 text-center">
            <span className="text-4xl block mb-2">{DEVICE_TYPE_INFO[scannedData.device_type]?.icon || '📦'}</span>
            <h3 className="font-semibold text-gray-900 text-lg">{DEVICE_TYPE_INFO[scannedData.device_type]?.label || scannedData.device_type}</h3>
            <p className="text-sm text-gray-500 mt-1">{DEVICE_TYPE_INFO[scannedData.device_type]?.desc}</p>
            <div className="mt-3 space-y-1 text-xs text-gray-500">
              <p>MAC: <span className="font-mono font-medium text-gray-700">{scannedData.mac_address}</span></p>
              <p>Device ID: <span className="font-mono font-medium text-gray-700">{scannedData.device_id}</span></p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Property</label>
            <select className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
              value={selectedPropertyId} onChange={e => setSelectedPropertyId(e.target.value)}>
              <option value="">Select a property...</option>
              {properties.map((p: any) => <option key={p.id} value={p.uuid || String(p.id)}>{p.name}</option>)}
            </select>
          </div>

          <Input label="Device Name" placeholder="e.g., Front Door Relay" value={deviceName}
            onChange={(e: any) => setDeviceName(e.target.value)} />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button onClick={handleAddDevice} disabled={saving || !selectedPropertyId || !deviceName}>
            {saving ? 'Registering...' : 'Register Device'}
          </Button>
        </div>
      )}

      {/* ═══ DONE ═══ */}
      {step === 'done' && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h3 className="font-semibold text-gray-900 text-lg mb-2">Device Registered!</h3>
          <p className="text-sm text-gray-500 mb-2">{deviceName} has been added to your property.</p>
          <p className="text-xs text-gray-400 mb-6">The device will appear as "Online" once it connects to WiFi and checks in with the Intrlock cloud.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => { setStep('scan'); setScannedData(null); }}>Add Another Device</Button>
            <Button variant="secondary" onClick={onClose}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
